import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffAPI } from '../services/api';
import resolutionsAPI from '../services/resolutionsApi';
import ErrorBoundary from '../components/ErrorBoundary';
import { todayLocal, parseCalendarDay } from '../utils/localDate';
import { formatApiError } from '../utils/errorMessage';

const ResolutionLetters = ({ selectedBusiness }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('resolutions'); // 'leadership' or 'resolutions'
  const [showLeadershipModal, setShowLeadershipModal] = useState(false);
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [editingLeadership, setEditingLeadership] = useState(null);
  const [editingResolution, setEditingResolution] = useState(null);
  const [selectedResolution, setSelectedResolution] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  // Leadership form data
  const [leadershipFormData, setLeadershipFormData] = useState({
    staff_id: '',
    position_title: '',
    position_order: '0',
    is_active: true,
    effective_date: todayLocal(),
    notes: ''
  });

  // Resolution form data
  const [resolutionFormData, setResolutionFormData] = useState({
    title: '',
    description: '',
    resolution_date: todayLocal(),
    meeting_date: '',
    meeting_location: '',
    meeting_type: 'board_meeting',
    status: 'draft',
    whereas_clauses: [''],
    resolved_clauses: [''],
    further_resolved_clauses: [],
    authorization_text: '',
    corporate_seal: false,
    secretary_certification: '',
    compliance_items: [],
    quorum_present: true,
    vote_count_for: '',
    vote_count_against: '',
    vote_count_abstain: '',
    problems: [{ matter_description: '', resolved_text: '', outcome_type: 'pending', authorization_level: 'board' }]
  });

  // Fetch staff for leadership selection
  const { data: staffData = [], isLoading: staffLoading } = useQuery({
    queryKey: ['staff', selectedBusiness],
    queryFn: async () => {
      try {
        const res = await staffAPI.getAll({ 
          business_id: selectedBusiness,
          employment_status: 'active'
        });
        return res.data || [];
      } catch (error) {
        console.error('Failed to load staff:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
        return [];
      }
    },
    enabled: !!selectedBusiness
  });

  // Fetch company leadership
  const { data: leadershipData = [], isLoading: leadershipLoading, error: leadershipError } = useQuery({
    queryKey: ['company-leadership', selectedBusiness],
    queryFn: async () => {
      try {
        const res = await resolutionsAPI.getLeadership(selectedBusiness);
        return res.data || [];
      } catch (error) {
        console.error('Failed to load leadership:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
        throw error;
      }
    },
    enabled: !!selectedBusiness
  });

  // Fetch resolutions
  const { data: resolutionsData = [], isLoading: resolutionsLoading, error: resolutionsError } = useQuery({
    queryKey: ['resolutions', selectedBusiness, statusFilter],
    queryFn: async () => {
      try {
        const res = await resolutionsAPI.getResolutions(selectedBusiness, statusFilter ? { status: statusFilter } : {});
        return res.data || [];
      } catch (error) {
        console.error('Failed to load resolutions:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
        throw error;
      }
    },
    enabled: !!selectedBusiness
  });

  // Create leadership mutation
  const createLeadershipMutation = useMutation({
    mutationFn: (data) => resolutionsAPI.createLeadership(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['company-leadership']);
      setShowLeadershipModal(false);
      resetLeadershipForm();
    },
    onError: (error) => {
      alert(formatApiError(error, 'Failed to create leadership'));
    }
  });

  // Update leadership mutation
  const updateLeadershipMutation = useMutation({
    mutationFn: ({ id, data }) => resolutionsAPI.updateLeadership(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['company-leadership']);
      setShowLeadershipModal(false);
      setEditingLeadership(null);
      resetLeadershipForm();
    },
    onError: (error) => {
      alert(formatApiError(error, 'Failed to update leadership'));
    }
  });

  // Delete leadership mutation
  const deleteLeadershipMutation = useMutation({
    mutationFn: (id) => resolutionsAPI.deleteLeadership(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['company-leadership']);
    },
    onError: (error) => {
      alert(formatApiError(error, 'Failed to delete leadership'));
    }
  });

  // Create resolution mutation
  const createResolutionMutation = useMutation({
    mutationFn: (data) => resolutionsAPI.createResolution(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['resolutions']);
      setShowResolutionModal(false);
      resetResolutionForm();
    },
    onError: (error) => {
      alert(formatApiError(error, 'Failed to create resolution'));
    }
  });

  // Update resolution mutation
  const updateResolutionMutation = useMutation({
    mutationFn: ({ id, data }) => resolutionsAPI.updateResolution(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['resolutions']);
      setShowResolutionModal(false);
      setEditingResolution(null);
      resetResolutionForm();
    },
    onError: (error) => {
      alert(formatApiError(error, 'Failed to update resolution'));
    }
  });

  // Delete resolution mutation
  const deleteResolutionMutation = useMutation({
    mutationFn: (id) => resolutionsAPI.deleteResolution(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['resolutions']);
    },
    onError: (error) => {
      alert(formatApiError(error, 'Failed to delete resolution'));
    }
  });

  // Sign resolution mutation
  const signResolutionMutation = useMutation({
    mutationFn: ({ resolutionId, leadershipId }) => 
      resolutionsAPI.signResolution(resolutionId, { leadership_id: leadershipId }),
    onSuccess: () => {
      queryClient.invalidateQueries(['resolutions']);
      if (selectedResolution) {
        loadResolutionDetails(selectedResolution.id);
      }
    },
    onError: (error) => {
      alert(formatApiError(error, 'Failed to sign resolution'));
    }
  });

  const resetLeadershipForm = () => {
    setLeadershipFormData({
      staff_id: '',
      position_title: '',
      position_order: '0',
      is_active: true,
      effective_date: todayLocal(),
      notes: ''
    });
    setEditingLeadership(null);
  };

  const handleStaffChange = (staffId) => {
    const selectedStaff = staffData.find(s => s.staff_id === Number(staffId));
    setLeadershipFormData({
      ...leadershipFormData,
      staff_id: staffId,
      position_title: selectedStaff?.job_title || ''
    });
  };

  const resetResolutionForm = () => {
    setResolutionFormData({
      title: '',
      description: '',
      resolution_date: todayLocal(),
      meeting_date: '',
      meeting_location: '',
      meeting_type: 'board_meeting',
      status: 'draft',
      whereas_clauses: [''],
      resolved_clauses: [''],
      further_resolved_clauses: [],
      authorization_text: '',
      corporate_seal: false,
      secretary_certification: '',
      compliance_items: [],
      quorum_present: true,
      vote_count_for: '',
      vote_count_against: '',
      vote_count_abstain: '',
      problems: [{ matter_description: '', resolved_text: '', outcome_type: 'pending', authorization_level: 'board' }]
    });
    setEditingResolution(null);
  };

  const handleEditLeadership = (leadership) => {
    setEditingLeadership(leadership);
    setLeadershipFormData({
      staff_id: leadership.staff_id,
      position_title: leadership.position_title,
      position_order: String(leadership.position_order || 0),
      is_active: leadership.is_active,
      effective_date: leadership.effective_date || todayLocal(),
      notes: leadership.notes || ''
    });
    setShowLeadershipModal(true);
  };

  const handleSubmitLeadership = async (e) => {
    e.preventDefault();
    try {
      const data = {
        business_id: selectedBusiness,
        ...leadershipFormData,
        position_order: parseInt(leadershipFormData.position_order) || 0
      };

      if (editingLeadership) {
        updateLeadershipMutation.mutate({ id: editingLeadership.id, data });
      } else {
        createLeadershipMutation.mutate(data);
      }
    } catch (error) {
      console.error('Submit error:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  const handleEditResolution = (resolution) => {
    setEditingResolution(resolution);
    // Load full resolution details
    loadResolutionDetails(resolution.id);
  };

  const toDateInput = (val) => {
    if (!val) return '';
    try {
      // Handles plain 'YYYY-MM-DD' or ISO strings like 'YYYY-MM-DDTHH:mm:ss.sssZ'
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const loadResolutionDetails = async (id) => {
    try {
      const res = await resolutionsAPI.getResolutionById(id);
      const resolution = res.data;
      setResolutionFormData({
        title: resolution.title || '',
        description: resolution.description || '',
        resolution_date: toDateInput(resolution.resolution_date) || todayLocal(),
        meeting_date: toDateInput(resolution.meeting_date),
        meeting_location: resolution.meeting_location || '',
        meeting_type: resolution.meeting_type || 'board_meeting',
        status: resolution.status || 'draft',
        whereas_clauses: resolution.whereas_clauses && resolution.whereas_clauses.length > 0 
          ? resolution.whereas_clauses 
          : [''],
        resolved_clauses: resolution.resolved_clauses && resolution.resolved_clauses.length > 0
          ? resolution.resolved_clauses
          : [''],
        further_resolved_clauses: resolution.further_resolved_clauses || [],
        authorization_text: resolution.authorization_text || '',
        corporate_seal: resolution.corporate_seal || false,
        secretary_certification: resolution.secretary_certification || '',
        compliance_items: resolution.compliance_items || [],
        quorum_present: resolution.quorum_present !== undefined ? resolution.quorum_present : true,
        vote_count_for: resolution.vote_count_for || '',
        vote_count_against: resolution.vote_count_against || '',
        vote_count_abstain: resolution.vote_count_abstain || '',
        problems: resolution.problems && resolution.problems.length > 0 
          ? resolution.problems.map(p => ({
              id: p.id,
              matter_description: p.matter_description || p.problem_description || '',
              resolved_text: p.resolved_text || '',
              outcome_type: p.outcome_type || p.status || 'pending',
              authorization_level: p.authorization_level || 'board',
              proposed_solution: p.proposed_solution || '',
              status: p.status || 'pending',
              resolution_date: toDateInput(p.resolution_date),
              resolution_notes: p.resolution_notes || ''
            }))
          : [{ matter_description: '', resolved_text: '', outcome_type: 'pending', authorization_level: 'board' }]
      });
      setEditingResolution(resolution);
      setSelectedResolution(resolution);
      setShowResolutionModal(true);
    } catch (error) {
      alert(formatApiError(error, 'Failed to load resolution details'));
    }
  };

  const handleSubmitResolution = async (e) => {
    e.preventDefault();
    
    if (!selectedBusiness) {
      alert('Please select a business first');
      return;
    }
    
    try {
      const problems = resolutionFormData.problems
        .filter(p => (p.matter_description || p.problem_description || '').trim())
        .map(p => ({
          id: p.id || undefined, // Include id if editing existing problem
          matter_description: p.matter_description || p.problem_description || '',
          resolved_text: p.resolved_text || null,
          outcome_type: p.outcome_type || p.status || 'pending',
          authorization_level: p.authorization_level || 'board',
          proposed_solution: p.proposed_solution || null,
          status: p.outcome_type || p.status || 'pending',
          resolution_date: p.resolution_date || null,
          resolution_notes: p.resolution_notes || null
        }));

      const data = {
        business_id: selectedBusiness,
        title: resolutionFormData.title,
        description: resolutionFormData.description || null,
        resolution_date: resolutionFormData.resolution_date,
        meeting_date: resolutionFormData.meeting_date || null,
        meeting_location: resolutionFormData.meeting_location || null,
        meeting_type: resolutionFormData.meeting_type || null,
        status: resolutionFormData.status,
        whereas_clauses: resolutionFormData.whereas_clauses.filter(c => c.trim()).length > 0 
          ? resolutionFormData.whereas_clauses.filter(c => c.trim()) 
          : null,
        resolved_clauses: resolutionFormData.resolved_clauses.filter(c => c.trim()).length > 0
          ? resolutionFormData.resolved_clauses.filter(c => c.trim())
          : null,
        further_resolved_clauses: resolutionFormData.further_resolved_clauses.filter(c => c.trim()).length > 0
          ? resolutionFormData.further_resolved_clauses.filter(c => c.trim())
          : null,
        authorization_text: resolutionFormData.authorization_text || null,
        corporate_seal: resolutionFormData.corporate_seal || false,
        secretary_certification: resolutionFormData.secretary_certification || null,
        compliance_items: resolutionFormData.compliance_items.filter(i => i.trim()).length > 0
          ? resolutionFormData.compliance_items.filter(i => i.trim())
          : null,
        quorum_present: resolutionFormData.quorum_present !== undefined ? resolutionFormData.quorum_present : true,
        vote_count_for: resolutionFormData.vote_count_for ? parseInt(resolutionFormData.vote_count_for) : null,
        vote_count_against: resolutionFormData.vote_count_against ? parseInt(resolutionFormData.vote_count_against) : null,
        vote_count_abstain: resolutionFormData.vote_count_abstain ? parseInt(resolutionFormData.vote_count_abstain) : null,
        problems: problems.length > 0 ? problems : undefined
      };

      if (editingResolution) {
        updateResolutionMutation.mutate({ id: editingResolution.id, data });
      } else {
        createResolutionMutation.mutate(data);
      }
    } catch (error) {
      console.error('Submit error:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Error submitting resolution'));
    }
  };

  const handleAddProblem = () => {
    setResolutionFormData({
      ...resolutionFormData,
      problems: [...resolutionFormData.problems, { matter_description: '', resolved_text: '', outcome_type: 'pending', authorization_level: 'board' }]
    });
  };

  const handleRemoveProblem = (index) => {
    if (resolutionFormData.problems.length === 1) return;
    setResolutionFormData({
      ...resolutionFormData,
      problems: resolutionFormData.problems.filter((_, i) => i !== index)
    });
  };

  const handleProblemChange = (index, field, value) => {
    
    // Use functional update to ensure we have the latest state
    setResolutionFormData(prevFormData => {
      const updatedProblems = [...prevFormData.problems];
      
      if (!updatedProblems[index]) {
        console.error('Problem at index', index, 'does not exist');
        return prevFormData;
      }
      
      // Create a new problem object with the updated field
      const updatedProblem = { ...updatedProblems[index], [field]: value };
      
      // Sync outcome_type with status for backward compatibility
      if (field === 'outcome_type') {
        updatedProblem.status = value;
      } else if (field === 'status' && !updatedProblem.outcome_type) {
        updatedProblem.outcome_type = value;
      }
      
      updatedProblems[index] = updatedProblem;
      
      
      return { ...prevFormData, problems: updatedProblems };
    });
  };

  // Update problem mutation
  const updateProblemMutation = useMutation({
    mutationFn: ({ resolutionId, problemId, data }) => 
      resolutionsAPI.updateProblem(resolutionId, problemId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['resolutions']);
      // Reload resolution details to get fresh data
      if (selectedResolution) {
        loadResolutionDetails(selectedResolution.id);
      }
    },
    onError: (error) => {
      console.error('Update problem mutation error:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      // Don't show alert here - handleSaveProblemStatus will show it
    }
  });

  const handleSaveProblemStatus = async (problem, index) => {
    if (!editingResolution) {
      alert('Please save the resolution first before updating individual matters');
      return;
    }

    // Always read from current state, not the passed problem (which might be stale)
    const currentProblem = resolutionFormData.problems[index];
    
    if (!currentProblem) {
      console.error('Problem not found at index', index);
      alert('Error: Problem not found. Please refresh and try again.');
      return;
    }
    
    if (!currentProblem.id) {
      alert('This matter has not been saved yet. Please save the resolution first, then you can update individual matters.');
      return;
    }
    
    
    try {
      // Build update data - only include fields that have values
      const updateData = {};
      
      // Always include matter_description if it exists
      const matterDesc = currentProblem.matter_description || currentProblem.problem_description;
      if (matterDesc !== undefined && matterDesc !== null && matterDesc !== '') {
        updateData.matter_description = matterDesc;
      }
      
      // Include other fields if they exist
      if (currentProblem.resolved_text !== undefined && currentProblem.resolved_text !== null) {
        updateData.resolved_text = currentProblem.resolved_text || null;
      }
      
      const outcomeType = currentProblem.outcome_type || currentProblem.status;
      if (outcomeType !== undefined && outcomeType !== null) {
        updateData.outcome_type = outcomeType;
        updateData.status = outcomeType; // Sync both
      }
      
      if (currentProblem.authorization_level !== undefined && currentProblem.authorization_level !== null) {
        updateData.authorization_level = currentProblem.authorization_level || 'board';
      }
      
      if (currentProblem.proposed_solution !== undefined) {
        updateData.proposed_solution = currentProblem.proposed_solution || null;
      }
      
      if (currentProblem.resolution_date !== undefined) {
        updateData.resolution_date = currentProblem.resolution_date || null;
      }
      
      if (currentProblem.resolution_notes !== undefined) {
        updateData.resolution_notes = currentProblem.resolution_notes || null;
      }
      
      // Ensure we have at least one field to update
      if (Object.keys(updateData).length === 0) {
        alert('No changes to save. Please make changes to the matter fields first.');
        return;
      }
      
      
      await updateProblemMutation.mutateAsync({
        resolutionId: editingResolution.id,
        problemId: currentProblem.id,
        data: updateData
      });
      
      alert('Matter updated successfully!');
      
      // Reload the resolution to get updated data
      if (selectedResolution) {
        await loadResolutionDetails(selectedResolution.id);
      }
    } catch (error) {
      console.error('Failed to save problem status:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Failed to update matter'));
    }
  };

  const handleGeneratePDF = async (resolutionId) => {
    try {
      const response = await resolutionsAPI.generatePDF(resolutionId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resolution-${resolutionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(formatApiError(error, 'Failed to generate PDF'));
    }
  };

  const handleSignResolution = async (resolutionId, leadershipId) => {
    if (!window.confirm('Are you sure you want to sign this resolution?')) return;

    try {
      await signResolutionMutation.mutateAsync({ resolutionId, leadershipId });
      alert('Resolution signed successfully!');
      
      // Reload the resolution to get updated signature status
      if (selectedResolution) {
        await loadResolutionDetails(selectedResolution.id);
      }
    } catch (error) {
      console.error('Failed to sign resolution:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Failed to sign resolution'));
    }
  };

  if (!selectedBusiness) {
    return (
      <ErrorBoundary section="Resolution Letters">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">Please select a business to manage resolution letters.</p>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary section="Resolution Letters">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Resolution Letters</h1>
          <p className="text-gray-600 mt-2">Manage corporate resolutions and company leadership</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('resolutions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'resolutions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Resolutions
            </button>
            <button
              onClick={() => setActiveTab('leadership')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'leadership'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Company Leadership
            </button>
          </nav>
        </div>

        {/* Resolutions Tab */}
        {activeTab === 'resolutions' && (
          <ErrorBoundary section="Resolutions List">
            <div className="mb-6 flex justify-between items-center">
              <div className="flex gap-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="pending_signatures">Pending Signatures</option>
                  <option value="signed">Signed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <button
                onClick={() => {
                  resetResolutionForm();
                  setShowResolutionModal(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                + New Resolution
              </button>
            </div>

            {resolutionsLoading ? (
              <div className="text-center py-8">Loading resolutions...</div>
            ) : resolutionsError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
                <p className="text-red-800">Error loading resolutions: {formatApiError(resolutionsError, 'Please refresh and try again.')}</p>
              </div>
            ) : resolutionsData.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <p className="text-gray-600">No resolutions found. Create your first resolution to get started.</p>
              </div>
            ) : (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matters</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signatures</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {resolutionsData.map((resolution) => (
                      <tr key={resolution.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {resolution.resolution_number || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{resolution.title}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {parseCalendarDay(resolution.resolution_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            resolution.status === 'signed' ? 'bg-green-100 text-green-800' :
                            resolution.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            resolution.status === 'pending_signatures' ? 'bg-yellow-100 text-yellow-800' :
                            resolution.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {resolution.status?.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {resolution.problem_count || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {resolution.signature_count || 0} / {resolution.required_signatures || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleEditResolution(resolution)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleGeneratePDF(resolution.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            PDF
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this resolution?')) {
                                deleteResolutionMutation.mutate(resolution.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ErrorBoundary>
        )}

        {/* Leadership Tab */}
        {activeTab === 'leadership' && (
          <ErrorBoundary section="Company Leadership">
            <div className="mb-6 flex justify-end">
              <button
                onClick={() => {
                  resetLeadershipForm();
                  setShowLeadershipModal(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                + Add Leadership Position
              </button>
            </div>

            {leadershipLoading ? (
              <div className="text-center py-8">Loading leadership...</div>
            ) : leadershipError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
                <p className="text-red-800">Error loading leadership: {formatApiError(leadershipError, 'Please refresh and try again.')}</p>
              </div>
            ) : leadershipData.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <p className="text-gray-600">No leadership positions found. Add company leadership to get started.</p>
              </div>
            ) : (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {leadershipData.map((leadership) => (
                      <tr key={leadership.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {leadership.position_title}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {leadership.full_name || `${leadership.first_name} ${leadership.last_name}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {leadership.position_order}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            leadership.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {leadership.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleEditLeadership(leadership)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this leadership position?')) {
                                deleteLeadershipMutation.mutate(leadership.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ErrorBoundary>
        )}

        {/* Leadership Modal */}
        {showLeadershipModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <ErrorBoundary section="Leadership Modal">
              <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-bold mb-4">
                  {editingLeadership ? 'Edit Leadership Position' : 'Add Leadership Position'}
                </h2>
                <form onSubmit={handleSubmitLeadership} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member *</label>
                    <select
                      required
                      value={leadershipFormData.staff_id}
                      onChange={(e) => handleStaffChange(e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      disabled={staffLoading}
                    >
                      <option value="">Select staff member</option>
                      {staffData.map((staff) => (
                        <option key={staff.staff_id} value={staff.staff_id}>
                          {staff.first_name} {staff.last_name} {staff.job_title ? `- ${staff.job_title}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Position Title</label>
                    <input
                      type="text"
                      value={leadershipFormData.position_title}
                      onChange={(e) => setLeadershipFormData({ ...leadershipFormData, position_title: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      placeholder="Auto-filled from job title (can be edited)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Defaults to staff member's job title. You can override for corporate governance positions (e.g., "President", "Secretary").
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Position Order</label>
                    <input
                      type="number"
                      value={leadershipFormData.position_order}
                      onChange={(e) => setLeadershipFormData({ ...leadershipFormData, position_order: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first in signature order</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
                    <input
                      type="date"
                      value={leadershipFormData.effective_date}
                      onChange={(e) => setLeadershipFormData({ ...leadershipFormData, effective_date: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={leadershipFormData.is_active}
                      onChange={(e) => setLeadershipFormData({ ...leadershipFormData, is_active: e.target.checked })}
                      className="mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">Active</label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={leadershipFormData.notes}
                      onChange={(e) => setLeadershipFormData({ ...leadershipFormData, notes: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      rows="3"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowLeadershipModal(false);
                        resetLeadershipForm();
                      }}
                      className="px-4 py-2 border rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createLeadershipMutation.isLoading || updateLeadershipMutation.isLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {createLeadershipMutation.isLoading || updateLeadershipMutation.isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </ErrorBoundary>
          </div>
        )}

        {/* Resolution Modal */}
        {showResolutionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <ErrorBoundary section="Resolution Modal">
              <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-bold mb-4">
                  {editingResolution ? 'Edit Resolution' : 'Create Resolution'}
                </h2>
                <form onSubmit={handleSubmitResolution} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      required
                      value={resolutionFormData.title}
                      onChange={(e) => setResolutionFormData({ ...resolutionFormData, title: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Resolution Date *</label>
                    <input
                      type="date"
                      required
                      value={resolutionFormData.resolution_date}
                      onChange={(e) => setResolutionFormData({ ...resolutionFormData, resolution_date: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Date</label>
                      <input
                        type="date"
                        value={resolutionFormData.meeting_date}
                        onChange={(e) => setResolutionFormData({ ...resolutionFormData, meeting_date: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Location</label>
                      <input
                        type="text"
                        value={resolutionFormData.meeting_location}
                        onChange={(e) => setResolutionFormData({ ...resolutionFormData, meeting_location: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={resolutionFormData.description}
                      onChange={(e) => setResolutionFormData({ ...resolutionFormData, description: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      rows="3"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Type</label>
                      <select
                        value={resolutionFormData.meeting_type || 'board_meeting'}
                        onChange={(e) => setResolutionFormData({ ...resolutionFormData, meeting_type: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="board_meeting">Board Meeting</option>
                        <option value="shareholder_meeting">Shareholder Meeting</option>
                        <option value="special_meeting">Special Meeting</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={resolutionFormData.status}
                        onChange={(e) => setResolutionFormData({ ...resolutionFormData, status: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="draft">Draft</option>
                        <option value="pending_signatures">Pending Signatures</option>
                        <option value="signed">Signed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>

                  {/* Voting Information */}
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quorum Present</label>
                      <select
                        value={resolutionFormData.quorum_present ? 'true' : 'false'}
                        onChange={(e) => setResolutionFormData({ ...resolutionFormData, quorum_present: e.target.value === 'true' })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Votes For</label>
                      <input
                        type="number"
                        value={resolutionFormData.vote_count_for || ''}
                        onChange={(e) => setResolutionFormData({ ...resolutionFormData, vote_count_for: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Votes Against</label>
                      <input
                        type="number"
                        value={resolutionFormData.vote_count_against || ''}
                        onChange={(e) => setResolutionFormData({ ...resolutionFormData, vote_count_against: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Abstentions</label>
                      <input
                        type="number"
                        value={resolutionFormData.vote_count_abstain || ''}
                        onChange={(e) => setResolutionFormData({ ...resolutionFormData, vote_count_abstain: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                  </div>

                  {/* WHEREAS Clauses */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">WHEREAS Clauses (Background/Context)</label>
                      <button
                        type="button"
                        onClick={() => setResolutionFormData({ ...resolutionFormData, whereas_clauses: [...resolutionFormData.whereas_clauses, ''] })}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add WHEREAS
                      </button>
                    </div>
                    {resolutionFormData.whereas_clauses.map((clause, index) => (
                      <div key={index} className="mb-2 flex gap-2">
                        <textarea
                          value={clause}
                          onChange={(e) => {
                            const newClauses = [...resolutionFormData.whereas_clauses];
                            newClauses[index] = e.target.value;
                            setResolutionFormData({ ...resolutionFormData, whereas_clauses: newClauses });
                          }}
                          className="flex-1 border border-gray-300 rounded px-3 py-2"
                          rows="2"
                          placeholder="WHEREAS, [provide background and context]..."
                        />
                        {resolutionFormData.whereas_clauses.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newClauses = resolutionFormData.whereas_clauses.filter((_, i) => i !== index);
                              setResolutionFormData({ ...resolutionFormData, whereas_clauses: newClauses });
                            }}
                            className="text-red-600 hover:text-red-800 px-2"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* RESOLVED Clauses */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">RESOLVED Clauses (Authorized Actions)</label>
                      <button
                        type="button"
                        onClick={() => setResolutionFormData({ ...resolutionFormData, resolved_clauses: [...resolutionFormData.resolved_clauses, ''] })}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add RESOLVED
                      </button>
                    </div>
                    {resolutionFormData.resolved_clauses.map((clause, index) => (
                      <div key={index} className="mb-2 flex gap-2">
                        <textarea
                          value={clause}
                          onChange={(e) => {
                            const newClauses = [...resolutionFormData.resolved_clauses];
                            newClauses[index] = e.target.value;
                            setResolutionFormData({ ...resolutionFormData, resolved_clauses: newClauses });
                          }}
                          className="flex-1 border border-gray-300 rounded px-3 py-2"
                          rows="2"
                          placeholder="[State the specific decision or action being approved]..."
                        />
                        {resolutionFormData.resolved_clauses.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newClauses = resolutionFormData.resolved_clauses.filter((_, i) => i !== index);
                              setResolutionFormData({ ...resolutionFormData, resolved_clauses: newClauses });
                            }}
                            className="text-red-600 hover:text-red-800 px-2"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* FURTHER RESOLVED Clauses */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">FURTHER RESOLVED Clauses (Additional Directives)</label>
                      <button
                        type="button"
                        onClick={() => setResolutionFormData({ ...resolutionFormData, further_resolved_clauses: [...resolutionFormData.further_resolved_clauses, ''] })}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add FURTHER RESOLVED
                      </button>
                    </div>
                    {resolutionFormData.further_resolved_clauses.map((clause, index) => (
                      <div key={index} className="mb-2 flex gap-2">
                        <textarea
                          value={clause}
                          onChange={(e) => {
                            const newClauses = [...resolutionFormData.further_resolved_clauses];
                            newClauses[index] = e.target.value;
                            setResolutionFormData({ ...resolutionFormData, further_resolved_clauses: newClauses });
                          }}
                          className="flex-1 border border-gray-300 rounded px-3 py-2"
                          rows="2"
                          placeholder="[Any additional actions or directives]..."
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newClauses = resolutionFormData.further_resolved_clauses.filter((_, i) => i !== index);
                            setResolutionFormData({ ...resolutionFormData, further_resolved_clauses: newClauses });
                          }}
                          className="text-red-600 hover:text-red-800 px-2"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Authorization Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Authorization Text</label>
                    <textarea
                      value={resolutionFormData.authorization_text}
                      onChange={(e) => setResolutionFormData({ ...resolutionFormData, authorization_text: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      rows="2"
                      placeholder="e.g., 'The President and Secretary are hereby authorized to execute all documents...'"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      State who is authorized to act (officers by title, not 'management')
                    </p>
                  </div>

                  {/* Compliance Items */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Compliance Items (DPOR, Zoning, etc.)</label>
                      <button
                        type="button"
                        onClick={() => setResolutionFormData({ ...resolutionFormData, compliance_items: [...resolutionFormData.compliance_items, ''] })}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add Item
                      </button>
                    </div>
                    {resolutionFormData.compliance_items.map((item, index) => (
                      <div key={index} className="mb-2 flex gap-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => {
                            const newItems = [...resolutionFormData.compliance_items];
                            newItems[index] = e.target.value;
                            setResolutionFormData({ ...resolutionFormData, compliance_items: newItems });
                          }}
                          className="flex-1 border border-gray-300 rounded px-3 py-2"
                          placeholder="e.g., DPOR Certificate, Zoning Approval, etc."
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newItems = resolutionFormData.compliance_items.filter((_, i) => i !== index);
                            setResolutionFormData({ ...resolutionFormData, compliance_items: newItems });
                          }}
                          className="text-red-600 hover:text-red-800 px-2"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Corporate Seal and Secretary Certification */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={resolutionFormData.corporate_seal}
                        onChange={(e) => setResolutionFormData({ ...resolutionFormData, corporate_seal: e.target.checked })}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">Corporate Seal Required</label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secretary Certification</label>
                    <textarea
                      value={resolutionFormData.secretary_certification}
                      onChange={(e) => setResolutionFormData({ ...resolutionFormData, secretary_certification: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      rows="2"
                      placeholder="e.g., 'I hereby certify that the foregoing is a true and correct copy of the resolution...'"
                    />
                  </div>

                  {/* Matters Section */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Matters Discussed</label>
                      <button
                        type="button"
                        onClick={handleAddProblem}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add Matter
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Add matters/issues that were discussed and decided upon in the meeting
                    </p>
                    {resolutionFormData.problems.map((problem, index) => {
                      // Always read from current state, not the map parameter
                      const currentProblem = resolutionFormData.problems[index] || problem;
                      return (
                      <div key={`problem-${currentProblem.id || index}-${index}`} className="mb-4 p-4 border border-gray-200 rounded">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Matter #{index + 1}</span>
                          {resolutionFormData.problems.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveProblem(index)}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Matter Description *</label>
                          <textarea
                            required
                            value={currentProblem.matter_description || currentProblem.problem_description || ''}
                            onChange={(e) => handleProblemChange(index, 'matter_description', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                            rows="2"
                            placeholder="Describe the matter/issue discussed..."
                          />
                        </div>
                        {editingResolution && currentProblem.id && (
                          <>
                            <div className="mb-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Outcome Type *</label>
                              <select
                                value={currentProblem.outcome_type || currentProblem.status || 'pending'}
                                onChange={(e) => handleProblemChange(index, 'outcome_type', e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                required
                              >
                                <optgroup label="A. APPROVED / AUTHORIZED ACTIONS">
                                  <option value="approved">Approved</option>
                                  <option value="authorized">Authorized</option>
                                  <option value="adopted">Adopted</option>
                                </optgroup>
                                <optgroup label="B. DENIED ACTIONS">
                                  <option value="denied">Not Adopted</option>
                                  <option value="rejected">Rejected</option>
                                  <option value="declined">Declined</option>
                                  <option value="failed">Failed</option>
                                </optgroup>
                                <optgroup label="C. DELAYED ACTIONS">
                                  <option value="tabled">Tabled</option>
                                  <option value="deferred">Deferred</option>
                                  <option value="pending">Pending</option>
                                </optgroup>
                              </select>
                              <p className="text-xs text-gray-500 mt-1">
                                Select the corporate governance outcome for this matter
                              </p>
                            </div>
                            <div className="mb-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">RESOLVED Clause Text</label>
                              <textarea
                                value={currentProblem.resolved_text || ''}
                                onChange={(e) => handleProblemChange(index, 'resolved_text', e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                rows="2"
                                placeholder="e.g., 'the Board hereby authorizes the President to...'"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                The actual RESOLVED clause text for approved/authorized matters
                              </p>
                            </div>
                            <div className="mb-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Authorization Level</label>
                              <select
                                value={currentProblem.authorization_level || 'board'}
                                onChange={(e) => handleProblemChange(index, 'authorization_level', e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                              >
                                <option value="board">Board of Directors</option>
                                <option value="officer">Officer (by Title)</option>
                                <option value="management">Management</option>
                                <option value="shareholder">Shareholder</option>
                              </select>
                            </div>
                            <div className="mb-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Decision Date</label>
                              <input
                                type="date"
                                value={currentProblem.resolution_date || ''}
                                onChange={(e) => handleProblemChange(index, 'resolution_date', e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                placeholder="Date when decision was made"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Date when this matter was decided (required for approved/denied status)
                              </p>
                            </div>
                            <div className="mb-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Decision Notes</label>
                              <textarea
                                value={currentProblem.resolution_notes || ''}
                                onChange={(e) => handleProblemChange(index, 'resolution_notes', e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                rows="2"
                                placeholder="Notes about the decision, voting results, conditions, etc."
                              />
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSaveProblemStatus(currentProblem, index);
                              }}
                              disabled={updateProblemMutation.isLoading || !editingResolution || !currentProblem.id}
                              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {updateProblemMutation.isLoading ? 'Saving...' : 'Save Status'}
                            </button>
                          </>
                        )}
                      </div>
                      );
                    })}
                  </div>

                  {/* Leadership/Signatures Section */}
                  <div className="border-t pt-4 mt-4">
                    <div className="mb-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Leadership Signatures</label>
                      <p className="text-xs text-gray-500 mb-3">
                        Leadership positions are automatically included from the <strong>Company Leadership</strong> tab. 
                        Active leadership positions will be required to sign this resolution.
                      </p>
                      {leadershipData.length === 0 ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                          No active leadership positions found. Add leadership positions in the <strong>Company Leadership</strong> tab first.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {leadershipData.filter(l => l.is_active).map((leadership) => (
                            <div key={leadership.id} className="flex items-center p-2 bg-gray-50 rounded text-sm">
                              <span className="font-medium">{leadership.position_title}: </span>
                              <span className="ml-2">{leadership.full_name || `${leadership.first_name} ${leadership.last_name}`}</span>
                              <span className="ml-auto text-xs text-gray-500">Order: {leadership.position_order}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {editingResolution && selectedResolution && selectedResolution.required_signatures && selectedResolution.required_signatures.length > 0 && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Signature Status</label>
                        <div className="space-y-2">
                          {selectedResolution.required_signatures.map((sig) => (
                            <div key={sig.leadership_id} className="flex justify-between items-center p-2 border border-gray-200 rounded">
                              <div>
                                <span className="font-medium">{sig.position_title}: </span>
                                <span>{sig.signer_name}</span>
                              </div>
                              {sig.is_signed ? (
                                <span className="text-sm text-green-600">✓ Signed</span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSignResolution(editingResolution.id, sig.leadership_id);
                                  }}
                                  className="text-sm text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
                                >
                                  Sign
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowResolutionModal(false);
                        resetResolutionForm();
                        setSelectedResolution(null);
                      }}
                      className="px-4 py-2 border rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createResolutionMutation.isLoading || updateResolutionMutation.isLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {createResolutionMutation.isLoading || updateResolutionMutation.isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </ErrorBoundary>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default ResolutionLetters;

